import {
  ChevronRight as ChevronIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'

import {
  QuestionnaireContext,
  QuestionnaireContextInterface,
} from '../../../providers/Questionnaire/Questionnaire.provider'

export interface QuestionnaireGroupSummary {
  questionnaireId: number
  title: string
  activeAssignment: boolean
  lastSubmissionDate: string | null
  totalSubmissions: number
}

interface QuestionnaireListProps {
  patientId: number
  setSelectedQuestionnaireSummary: React.Dispatch<
    React.SetStateAction<QuestionnaireGroupSummary>
  >
}

export const QuestionnaireList: React.FC<QuestionnaireListProps> = ({
  patientId,
  setSelectedQuestionnaireSummary,
}) => {
  const { getQuestionnaireGroupSummaries, questionnaireGroupSummaries } =
    useContext(QuestionnaireContext) as QuestionnaireContextInterface

  const [loading, setLoading] = useState(true)

  const fetchData = async () => {
    await getQuestionnaireGroupSummaries(patientId)
    setLoading(false)
  }

  useEffect(() => {
    fetchData()
  }, [patientId])

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (questionnaireGroupSummaries.length === 0) {
    return (
      <Alert severity='info'>No questionnaires found for this patient.</Alert>
    )
  }

  return (
    <Box>
      <Typography variant='h6' fontWeight={600} sx={{ mb: 2 }}>
        Patient Questionnaires
      </Typography>

      <Stack spacing={1.5}>
        {questionnaireGroupSummaries.map((summary) => (
          <Card key={summary.questionnaireId} variant='outlined'>
            <CardActionArea
              onClick={() => setSelectedQuestionnaireSummary(summary)}
            >
              <CardContent>
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                >
                  <Stack direction='row' alignItems='center' spacing={2}>
                    <DotIcon
                      sx={{
                        fontSize: 12,
                        color: summary.activeAssignment
                          ? 'success.main'
                          : 'text.disabled',
                      }}
                    />
                    <Box>
                      <Typography
                        variant='subtitle1'
                        fontWeight={600}
                        fontSize={16}
                      >
                        {summary.title}
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        fontSize={15}
                      >
                        {summary.totalSubmissions} submission
                        {summary.totalSubmissions !== 1 ? 's' : ''}
                        {summary.lastSubmissionDate &&
                          ` · Last: ${formatDate(summary.lastSubmissionDate)}`}
                        {!summary.lastSubmissionDate &&
                          summary.totalSubmissions === 0 &&
                          ' · No responses yet'}
                      </Typography>
                    </Box>
                  </Stack>

                  <ChevronIcon color='action' />
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    </Box>
  )
}

// Helpers
const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
